import { useScrollAnimation } from '@/hooks/use-scroll-animation'
import LazyVimeoEmbed from '@/components/LazyVimeoEmbed'
import featuraPapel400g from '@/assets/feature-papel-400g.webp'
import featuraPapel800g from '@/assets/feature-papel-800g.webp'
import designAtemporal from '@/assets/design-atemporal-2.webp'

const PhotobookFeatures = () => {
  const { ref, isVisible } = useScrollAnimation()

  const features = [
    {
      title: 'Abertura 180º',
      videoId: '1147378738',
      highlight: 'A imagem, inteira e completa.',
      description:
        'Paisagens que se desdobram por completo, sorrisos que não se partem ao meio. A tecnologia de abertura 180° respeita a integridade da sua fotografia, oferecendo uma superfície totalmente plana que valoriza a cena e transforma a maneira como você revive cada momento.',
    },
    {
      title: 'Papel Fotográfico 400g e 800g',
      images: [featuraPapel400g, featuraPapel800g],
      highlight: 'Cores que permanecem, memórias que se pode sentir.',
      description:
        'Usamos apenas papel fotográfico Fujifilm, uma referência mundial em nitidez e cores vibrantes. Escolha entre a sofisticação do papel 400g ou a presença imponente do 800g, com 1mm de espessura. É a garantia de que a vivacidade das suas memórias resistirá ao tempo, em páginas feitas para serem tocadas.',
    },
    {
      title: 'Design Atemporal e Acabamento Impecável',
      image: designAtemporal,
      highlight: 'Um design que serve à memória.',
      description:
        'Nosso processo combina layout profissional e acabamentos cuidadosos para criar um álbum que carrega o valor das suas lembranças, transformando histórias em um objeto de design atemporal, feito para existir hoje e sempre.',
    },
  ]

  return (
    <section className="py-20 bg-gradient-to-br from-muted/40 via-muted/20 to-background">
      <div
        ref={ref}
        className={`container mx-auto px-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <h2 className="font-serif font-semibold text-3xl md:text-4xl text-center mb-2 tracking-tight text-foreground">
          Design que Honra Sua <span className="text-gold italic">Memória</span>
        </h2>
        <p className="text-center text-muted-foreground max-w-4xl mx-auto mb-10 text-base leading-relaxed">
          Acreditamos que a qualidade não é um luxo, mas um direito. Por isso,
          cada álbum é criado com materiais de primeira linha, acabamentos de
          precisão e um design que coloca sua história no centro. Sofisticação
          pensada para durar gerações.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`text-center space-y-4 transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: isVisible ? `${index * 150}ms` : '0ms',
              }}
            >
              <div className="aspect-square bg-muted rounded-2xl overflow-hidden mb-4">
                {feature.videoId ? (
                  <LazyVimeoEmbed
                    videoId={feature.videoId}
                    startTime={15}
                    endTime={17}
                    className="w-full h-full"
                  />
                ) : feature.images ? (
                  <div className="w-full h-full grid grid-rows-2 gap-1">
                    {feature.images.map((img, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="relative w-full h-full overflow-hidden"
                      >
                        <img
                          src={img}
                          alt={`${feature.title} ${imgIndex + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute top-2 left-2 bg-secondary text-secondary-foreground text-xs font-bold px-2 py-1 rounded">
                          {imgIndex === 0 ? '400g' : '800g'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : feature.image ? (
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    Imagem
                  </div>
                )}
              </div>
              <h3 className="text-xl font-bold">{feature.title}</h3>
              {feature.highlight && (
                <p className="text-sm font-bold text-gold">
                  {feature.highlight}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default PhotobookFeatures
